import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import BalanceDiario from "../components/BalanceDiario";
import BalanceSemanal from "../components/BalanceSemanal";
import ManageGastos from "../components/ManageGastos";
import { UserContext } from "../services/UserContext";
import { useClient } from "../services/ClientContext";
import { API } from "../services/config";
import "./FinanzasPage.css";

export default function FinanzasPage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { multiProfesional } = useClient();
  const [token, setToken] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [profesionalId, setProfesionalId] = useState(null);
  const [showBalanceDiario, setShowBalanceDiario] = useState(false);
  const [showBalanceSemanal, setShowBalanceSemanal] = useState(false);
  const [showGastos, setShowGastos] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      navigate("/login");
    } else {
      setToken(storedToken);
      fetchTurnos(storedToken, null);
      fetchProfesionales(storedToken);
    }
  }, [navigate]);

  useEffect(() => {
    if (!token || !user) return;
    if (user.role === "profesional") {
      setProfesionalId(user.profesional?.id || null);
      fetchTurnos(token, user.profesional?.id || null);
    }
  }, [token, user]);

  const fetchTurnos = async (token, selectedProfesionalId) => {
    try {
      const qs = selectedProfesionalId ? `?profesional_id=${selectedProfesionalId}` : "";
      const response = await fetch(`${API}/turnos${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTurnos(data.data);
      }
    } catch (err) {
      console.error("Error al cargar turnos:", err);
    }
  };

  const fetchProfesionales = async (token) => {
    try {
      const response = await fetch(`${API}/profesionales`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setProfesionales(data.data.filter((p) => p.activo));
      }
    } catch (err) {
      console.error("Error al cargar profesionales:", err);
    }
  };

  const profesionalActual = useMemo(
    () => profesionales.find((p) => String(p.id) === String(profesionalId)),
    [profesionalId, profesionales]
  );

  if (!token) return <div>Cargando...</div>;

  return (
    <div className="finanzas-container">
      <Sidebar />
      <div className="finanzas-content">
        <div className="finanzas-header">
          <h1>💰 Finanzas y Reportes</h1>
          <p className="finanzas-subtitle">Gestiona tus ingresos, gastos y reportes</p>
          {multiProfesional && user?.role === "admin" ? (
            <select
              className="select-profesional"
              value={profesionalId || ""}
              onChange={(e) => {
                const nextId = e.target.value ? Number(e.target.value) : null;
                setProfesionalId(nextId);
                fetchTurnos(token, nextId);
              }}
            >
              <option value="">Todos los profesionales</option>
              {profesionales.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.apellido}, {p.nombre}
                </option>
              ))}
            </select>
          ) : (
            <p className="finanzas-subtitle">
              Profesional: {profesionalActual ? `${profesionalActual.apellido}, ${profesionalActual.nombre}` : "Mi cuenta"}
            </p>
          )}
        </div>

        <div className="finanzas-grid">
          <div className="finanzas-card">
            <div className="card-icon">📅</div>
            <h2>Balance Diario</h2>
            <p>Ver cobros del día actual</p>
            <button
              className="card-btn"
              onClick={() => setShowBalanceDiario(true)}
            >
              Ver Hoy
            </button>
          </div>

          <div className="finanzas-card">
            <div className="card-icon">📊</div>
            <h2>Balance Semanal</h2>
            <p>Ingresos, gastos y margen semanal</p>
            <button
              className="card-btn"
              onClick={() => setShowBalanceSemanal(true)}
            >
              Ver Reporte
            </button>
          </div>

          <div className="finanzas-card">
            <div className="card-icon">💸</div>
            <h2>Gestión de Gastos</h2>
            <p>Registra sueldos, insumos y servicios</p>
            <button
              className="card-btn"
              onClick={() => setShowGastos(true)}
            >
              Gestionar
            </button>
          </div>
        </div>

        <div className="finanzas-info">
          <div className="info-box">
            <h3>ℹ️ Información General</h3>
            <ul>
              <li>📅 <strong>Balance Diario:</strong> Muestra todos los cobros registrados hoy.</li>
              <li>📊 <strong>Balance Semanal:</strong> Resumen de ingresos, gastos y margen de ganancias. Opción de enviar por email.</li>
              <li>💸 <strong>Gastos:</strong> Registra y gestiona gastos por categoría (sueldos, insumos, servicios, otros).</li>
            </ul>
          </div>
        </div>
      </div>

      {showBalanceDiario && (
        <BalanceDiario
          onClose={() => setShowBalanceDiario(false)}
          turnos={turnos}
          profSel={profesionalId || "Todos"}
        />
      )}

      {showBalanceSemanal && (
        <BalanceSemanal
          onClose={() => setShowBalanceSemanal(false)}
          token={token}
          profesionalId={profesionalId}
        />
      )}

      {showGastos && (
        <ManageGastos
          onClose={() => setShowGastos(false)}
          token={token}
          profesionalId={profesionalId}
        />
      )}
    </div>
  );
}
